import { Box } from "@mui/material";
import { useCallback } from "react";

type Props = {
    current: number;
    setStep: (n: number) => void;
};

export default function ResultsSidebar({ current, setStep }: Props) {
    const steps = [
        { id: 1, label: "Profile" },
        { id: 2, label: "Plan" },
        { id: 3, label: "Meals" },
        { id: 4, label: "Treats" },
        { id: 5, label: "Review" },
    ];

    const clickStep = useCallback((stepId: number) => setStep(stepId), [setStep]);

    return (
        <>
            {/* ✅ MOBILE + TABLET: horizontal stepper bar */}
            <Box
                sx={{
                    display: { xs: "block", lg: "none" },
                    width: "100%",
                    bgcolor: "#FAF5EB",
                    borderRadius: "16px",
                    border: "1px solid rgba(74,48,38,0.10)",
                    boxShadow: "0 6px 25px rgba(0,0,0,0.05)",
                    px: 1.25,
                    py: 1.25,
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        overflowX: "auto",
                        WebkitOverflowScrolling: "touch",
                        scrollBehavior: "smooth",
                        pb: 0.25,

                        // nicer scrollbars (optional)
                        "&::-webkit-scrollbar": { height: "6px" },
                        "&::-webkit-scrollbar-thumb": {
                            background: "#d0c4ae",
                            borderRadius: "10px",
                        },
                    }}
                >
                    {steps.map((s) => {
                        const isActive = current === s.id;
                        const isCompleted = s.id < current;

                        const bgColor = isActive
                            ? "#FF7874"
                            : isCompleted
                                ? "#FFE1DD"
                                : "#F4F0E8";

                        const textColor = isActive
                            ? "#FFFFFF"
                            : isCompleted
                                ? "#7B3306"
                                : "#A88D7A";

                        return (
                            <Box
                                key={s.id}
                                onClick={() => clickStep(s.id)}
                                onTouchEnd={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    clickStep(s.id);
                                }}
                                sx={{
                                    flex: "0 0 auto",
                                    cursor: "default",
                                    touchAction: "manipulation",
                                    userSelect: "none",
                                    WebkitUserSelect: "none",
                                    WebkitTapHighlightColor: "transparent",
                                    textAlign: "center",
                                    px: 1.4,
                                    py: 1,
                                    borderRadius: "999px",
                                    backgroundColor: bgColor,
                                    color: textColor,
                                    fontWeight: 800,
                                    fontSize: "13px",
                                    letterSpacing: 0.2,
                                    transition: "all 0.2s",
                                    whiteSpace: "nowrap",
                                    border: isActive ? "2px solid rgba(123,51,6,0.35)" : "2px solid transparent",
                                    "&:active": { transform: "scale(0.98)" },
                                }}
                            >
                                {s.label}
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {/* ✅ DESKTOP ONLY: vertical sidebar (1200px+) */}
            <Box
                sx={{
                    display: { xs: "none", lg: "flex" },
                    bgcolor: "#FAF5EB",
                    borderRadius: "24px",
                    py: 4,
                    px: 3,
                    width: "100%",
                    maxWidth: "260px",
                    height: "100%",
                    border: "1px solid rgba(74,48,38,0.10)",
                    boxShadow: "0 6px 25px rgba(0,0,0,0.05)",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    minHeight: "523px",
                }}
            >
                {/* Top Steps */}
                <Box>
                    {steps.map((s, index) => {
                        const isActive = current === s.id;
                        const isCompleted = s.id < current;

                        const bgColor = isActive
                            ? "#FF7874"
                            : isCompleted
                                ? "#FFE1DD"
                                : "#F4F0E8";

                        const textColor = isActive
                            ? "#FFFFFF"
                            : isCompleted
                                ? "#7B3306"
                                : "#A88D7A";

                        const lineColor = isCompleted ? "#FF7874" : "#E1D6C8";

                        return (
                            <Box
                                key={s.id}
                                sx={{ width: "100%", mb: index < steps.length - 1 ? 3 : 0 }}
                            >
                                <Box
                                    onClick={() => clickStep(s.id)}
                                    onTouchEnd={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        clickStep(s.id);
                                    }}
                                    sx={{
                                        cursor: "default",
                                        touchAction: "manipulation",
                                        userSelect: "none",
                                        WebkitUserSelect: "none",
                                        WebkitTapHighlightColor: "transparent",
                                        textAlign: "center",
                                        py: 1.8,
                                        borderRadius: "14px",
                                        backgroundColor: bgColor,
                                        color: textColor,
                                        fontWeight: 700,
                                        fontSize: "15px",
                                        letterSpacing: 0.3,
                                        transition: "all 0.25s",
                                        "&:hover": {
                                            backgroundColor: isActive
                                                ? "#F38E82"
                                                : isCompleted
                                                    ? "#FFD2CC"
                                                    : "#EFE7DB",
                                        },
                                    }}
                                >
                                    {s.label}
                                </Box>

                                {/* PROGRESS LINE */}
                                {index < steps.length - 1 && (
                                    <Box
                                        sx={{
                                            width: "2px",
                                            height: "28px",
                                            backgroundColor: lineColor,
                                            mx: "auto",
                                            mt: 1,
                                            transition: "background-color 0.3s ease",
                                        }}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {/* Logo Bottom */}
                <Box
                    component="img"
                    src="https://cdn.shopify.com/s/files/1/0946/0622/9844/files/Logo.png?v=1756962889"
                    alt="Paws on Plates"
                    sx={{
                        width: "140px",
                        opacity: 0.8,
                        mx: "auto",
                        mt: 4,
                    }}
                />
            </Box>
        </>
    );
}